import React, { useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link, useNavigate } from 'react-router-dom';
import { getCompanyStats } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HeroSection from './HeroSection';
import ScoreStatCard from './ScoreStatCard';
import { trackGoogleAdsConversion } from '../lib/googleAds';
import '../styles/landing.css';

function LandingPage() {
  const { loginWithRedirect, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const [companyCount, setCompanyCount] = useState(0);
  const [statsStatus, setStatsStatus] = useState('loading');
  const [displayedCount, setDisplayedCount] = useState(0);
  const [displayedScore, setDisplayedScore] = useState(0);
  const [visibleSections, setVisibleSections] = useState({});
  const countRef = useRef(20000);
  const sectionRefs = useRef({});

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const wakeupTimeout = setTimeout(() => {
      setStatsStatus((currentStatus) => (currentStatus === 'loading' ? 'waking' : currentStatus));
    }, 4500);

    getCompanyStats()
      .then((data) => {
        if (typeof data.totalCompanies === 'number') {
          countRef.current = data.totalCompanies;
          setCompanyCount(data.totalCompanies);
        }
        setStatsStatus('ready');
      })
      .catch((err) => {
        console.error('Failed to fetch company stats, backend may be sleeping', err);
        setStatsStatus('waking');
      });

    return () => clearTimeout(wakeupTimeout);
  }, []);

  // Animated counter effect
  useEffect(() => {
    if (companyCount === 0) return;
    
    let startValue = 0;
    const duration = 2000;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = Math.floor(startValue + (companyCount - startValue) * progress);
      setDisplayedCount(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  }, [companyCount]);

  // Animated score effect when scoring section becomes visible
  useEffect(() => {
    if (!visibleSections.scoring) return;
    
    let startValue = 0;
    const endValue = 85;
    const duration = 2000;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const current = Math.floor(startValue + (endValue - startValue) * progress);
      setDisplayedScore(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  }, [visibleSections.scoring]);

  useEffect(() => {
    if (visibleSections.pricing) {
      trackGoogleAdsConversion('pricing_intelligence_viewed', { once: true });
    }
  }, [visibleSections.pricing]);

  // Scroll listener for parallax and section visibility
  useEffect(() => {
    
    // Intersection Observer for section animations
    // Tune section reveal timing: require more of the section visible before triggering
    const observerOptions = {
      threshold: 0.25,
      rootMargin: '0px 0px -120px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleSections((prev) => ({
            ...prev,
            [entry.target.id]: true
          }));
        }
      });
    }, observerOptions);

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleJoinClick = () => {
    trackGoogleAdsConversion('signup_intent');

    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      loginWithRedirect();
    }
  };

  const handleIntelligenceClick = () => {
    trackGoogleAdsConversion('signup_intent');

    if (isAuthenticated) {
      navigate('/dashboard/company-settings');
    } else {
      loginWithRedirect();
    }
  };

  return (
    <div className="landing-page">
      <Header />
      
      {/* Hero Section - Kinetic Vault Design */}
      <HeroSection 
        isAuthenticated={isAuthenticated}
        displayedCount={displayedCount}
        companyCount={companyCount}
        statsStatus={statsStatus}
        handleJoinClick={handleJoinClick}
      />

      {/* Risqly Security Badge - Browser Mockup */}
      <section
        className={`section section-transparent ${visibleSections.badge ? 'visible' : ''}`}
        ref={(el) => (sectionRefs.current.badge = el)}
        id="badge"
      >
        <div className="badge-section-wrapper" style={{
          animation: visibleSections.badge ? 'slideInUp 0.6s ease-out 0.2s both' : 'none',
          opacity: visibleSections.badge ? 1 : 0
        }}>
            <h2 className="badge-section-title" style={{
            animation: visibleSections.badge ? 'fadeInUp 0.6s ease-out both' : 'none',
            opacity: visibleSections.badge ? 1 : 0
          }}>
            Display your Security Score to build customer trust
          </h2>
          
          <div className="browser-mockup" style={{
            animation: visibleSections.badge ? 'fadeInUp 0.6s ease-out 0.12s both' : 'none',
            opacity: visibleSections.badge ? 1 : 0
          }}>
            <div className="browser-header">
              <div className="browser-buttons">
                <div className="browser-button red"></div>
                <div className="browser-button yellow"></div>
                <div className="browser-button green"></div>
              </div>
              <div className="browser-url">example-company.com</div>
            </div>
            
            <div className="browser-content">
              <div className="mockup-header">
                <div className="mockup-logo"></div>
                <div className="mockup-nav">
                  <div className="mockup-nav-item"></div>
                  <div className="mockup-nav-item"></div>
                  <div className="mockup-nav-item"></div>
                </div>
              </div>
              
              <div className="mockup-body">
                <div className="mockup-text-block"></div>
                <div className="mockup-text-block short"></div>
              </div>
              
              <div className="mockup-footer">
                <div className="mockup-footer-text"></div>
                <div className="mockup-footer-text short"></div>
                <a
                  className="badge-in-footer"
                  href="https://risqly.net/company/Risqly"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open Risqly company score page"
                >
                  <span className="material-symbols-outlined badge-shield-icon-small">security</span>
                  <span className="badge-label-small">See Our Risqly Score</span>
                </a>
              </div>
            </div>
          </div>
          
          <p className="badge-description" style={{
            animation: visibleSections.badge ? 'fadeInUp 0.6s ease-out 0.24s both' : 'none',
            opacity: visibleSections.badge ? 1 : 0
          }}>
            Add a Risqly Security Score badge to your website in minutes. Your customers can verify your security posture with a single click.
          </p>
        </div>
      </section>

      <section
        className={`section section-transparent ${visibleSections.companyPreview ? 'visible' : ''}`}
        ref={(el) => (sectionRefs.current.companyPreview = el)}
        id="companyPreview"
      >
        <div className="section-header" style={{
          opacity: visibleSections.companyPreview ? 1 : 0,
          animation: visibleSections.companyPreview ? 'fadeInUp 0.6s ease-out' : 'none',
          transition: 'opacity 0.6s ease-out'
        }}>
          <h2 className="section-label">Browse Companies</h2>
          <h3 className="section-title-landing">See companies the way buyers see them</h3>
          <p className="section-description">
            A quick preview of the company list, with scores and verification indicators.
          </p>
          <Link to="/companies" className="company-preview-link">
            View the full companies list
          </Link>
        </div>
        <div
          className="company-preview-frame"
          style={{
            animation: visibleSections.companyPreview ? 'fadeInUp 0.6s ease-out 0.12s both' : 'none',
            opacity: visibleSections.companyPreview ? 1 : 0
          }}
        >
          <div className="company-preview-list">
            {[
              { name: 'Northwind', domain: 'northwind.com', scores: [86, 71, 79, 67] },
              { name: 'Aperture', domain: 'aperture.io', scores: [74, 60, 68, 55] },
              { name: 'Waystar', domain: 'waystar.co', scores: [48, 42, 51, 39] },
              { name: 'BluePeak', domain: 'bluepeak.net', scores: [69, 58, 73, 62] },
              { name: 'CloudForge', domain: 'cloudforge.ai', scores: [81, 67, 76, 64] },
              { name: 'Monarch', domain: 'monarch.io', scores: [59, 49, 63, 52] },
              { name: 'HexaRail', domain: 'hexarail.com', scores: [77, 64, 72, 58] },
              { name: 'Evergreen', domain: 'evergreen.co', scores: [66, 55, 69, 50] },
            ].map((company) => (
              <div key={company.name} className="company-preview-card">
                <div className="company-preview-card-top">
                  <div className="company-preview-copy">
                    <div className="company-preview-name">{company.name}</div>
                    <div className="company-preview-domain">{company.domain}</div>
                  </div>
                </div>
                <div className="company-preview-metrics" aria-label={`${company.name} score preview`}>
                  {[
                    { title: 'Security', value: company.scores[0], colorClass: 'ring-green' },
                    { title: 'Transparency', value: company.scores[1], colorClass: 'ring-orange' },
                    { title: 'Policy', value: company.scores[2], colorClass: 'ring-blue' },
                    { title: 'External', value: company.scores[3], colorClass: 'ring-pink' },
                  ].map((metric) => (
                    <div key={metric.title} className="company-preview-score-card">
                      <span className={`company-preview-score-ring ${metric.colorClass}`} aria-hidden="true">
                        <span className="company-preview-score-ring-value">{metric.value}</span>
                      </span>
                      <span className="company-preview-score-title">{metric.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className={`section ${visibleSections.scoring ? 'visible' : ''}`}
        ref={(el) => (sectionRefs.current.scoring = el)}
        id="scoring"
      >
        <div className="score-explainer-layout">
          <div className="score-explainer-copy">
            <h2 className="section-label" style={{
              animation: visibleSections.scoring ? 'fadeInLeft 0.6s ease-out' : 'none',
              opacity: visibleSections.scoring ? 1 : 0
            }}>
              Score Breakdown
            </h2>
            <h3 className="section-title-landing" style={{
              animation: visibleSections.scoring ? 'fadeInLeft 0.6s ease-out 0.1s both' : 'none',
              opacity: visibleSections.scoring ? 1 : 0
            }}>
                See What Drives the Security Score
            </h3>
            <p className="content-text" style={{
              animation: visibleSections.scoring ? 'fadeInLeft 0.6s ease-out 0.2s both' : 'none',
              opacity: visibleSections.scoring ? 1 : 0
            }}>
                Risqly starts every company at 100 and only subtracts penalties from the strongest risk signals.
                The rating combines Policy Compliance, External Integrity, Transparency, and dependency-network
                evidence, recalculated regularly so the signal stays current.
            </p>
            <div className="score-meaning-grid">
                <article className="score-meaning-item">
                  <h4>Total Security Score</h4>
                  <p>Combined signal of policy quality, external technical posture, and dependency-network evidence.</p>
                </article>
              <article className="score-meaning-item">
                  <h4>Transparency</h4>
                  <p>How much of a company's declared vendor chain is visible through participating vendors.</p>
              </article>
              <article className="score-meaning-item">
                  <h4>Policy Compliance</h4>
                  <p>Framework alignment, policy coverage, governance clarity, and execution evidence.</p>
              </article>
              <article className="score-meaning-item">
                  <h4>External Integrity</h4>
                  <p>Publicly observable controls such as DNS hygiene, TLS posture, email authentication, and recent exposure risk.</p>
              </article>
            </div>
          </div>
          <div className="score-sample-grid">
            <ScoreStatCard
              title="Total Security Score"
                description="Shareable rating"
              value={displayedScore}
              ringValue={displayedScore}
              color="#00ff41"
            />
            <ScoreStatCard
                title="Transparency"
                description="Declared vendor-chain visibility"
              value={72}
              ringValue={72}
              color="#ff9500"
              suffix="%"
              showMax={false}
            />
            <ScoreStatCard
                title="Policy Compliance"
                description="Framework alignment, policy coverage, governance clarity"
              value={79}
              ringValue={79}
              color="#00bcd4"
              showMax={false}
            />
            <ScoreStatCard
                title="External Integrity"
                description="DNS hygiene, TLS posture, email authentication, and exposure risk"
              value={67}
              ringValue={67}
              color="#e91e63"
              showMax={false}
            />
          </div>
        </div>
      </section>

      {/* How Risqly Works */}
      <section
        className={`section section-transparent ${visibleSections.model ? 'visible' : ''}`}
        ref={(el) => (sectionRefs.current.model = el)}
        id="model"
      >
        <div className="comparison-layout">
          <div
            className="comparison-card"
            style={{
              animation: visibleSections.model ? 'slideInUp 0.6s ease-out 0.2s both' : 'none',
              opacity: visibleSections.model ? 1 : 0
            }}
          >
            <div className="comparison-table-wrapper">
              <table className="comparison-table" aria-label="Competition versus Risqly comparison">
                <thead>
                  <tr>
                    <th>Capability</th>
                    <th>Competition</th>
                    <th className="comparison-risqly-header">Risqly</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">Public company ratings</th>
                    <td><span className="compare-icon compare-icon-checked material-symbols-outlined" aria-label="Checked">check_box</span></td>
                    <td><span className="compare-icon compare-icon-checked compare-icon-risqly material-symbols-outlined" aria-label="Checked">check_box</span></td>
                  </tr>
                  <tr>
                    <th scope="row">External integrity assessment</th>
                    <td><span className="compare-icon compare-icon-checked material-symbols-outlined" aria-label="Checked">check_box</span></td>
                    <td><span className="compare-icon compare-icon-checked compare-icon-risqly material-symbols-outlined" aria-label="Checked">check_box</span></td>
                  </tr>
                  <tr>
                    <th scope="row">3rd-party vendor risk assessment</th>
                    <td><span className="compare-icon compare-icon-checked material-symbols-outlined" aria-label="Checked">check_box</span></td>
                    <td><span className="compare-icon compare-icon-checked compare-icon-risqly material-symbols-outlined" aria-label="Checked">check_box</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Nth-party vendor risk assessment</th>
                    <td><span className="compare-icon compare-icon-unchecked material-symbols-outlined" aria-label="Not checked">check_box_outline_blank</span></td>
                    <td><span className="compare-icon compare-icon-checked compare-icon-risqly material-symbols-outlined" aria-label="Checked">check_box</span></td>
                  </tr>
                  <tr>
                    <th scope="row">AI-driven security improvements</th>
                    <td><span className="compare-icon compare-icon-unchecked material-symbols-outlined" aria-label="Not checked">check_box_outline_blank</span></td>
                    <td><span className="compare-icon compare-icon-checked compare-icon-risqly material-symbols-outlined" aria-label="Checked">check_box</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Compliance guidance and action plan</th>
                    <td><span className="compare-icon compare-icon-unchecked material-symbols-outlined" aria-label="Not checked">check_box_outline_blank</span></td>
                    <td><span className="compare-icon compare-icon-checked compare-icon-risqly material-symbols-outlined" aria-label="Checked">check_box</span></td>
                  </tr>
                  <tr>
                    <th scope="row">Free security rating</th>
                    <td><span className="compare-icon compare-icon-unchecked material-symbols-outlined" aria-label="Not checked">check_box_outline_blank</span></td>
                    <td><span className="compare-icon compare-icon-checked compare-icon-risqly material-symbols-outlined" aria-label="Checked">check_box</span></td>
                  </tr>
                  <tr>
                    <th scope="row">10 minute onboarding</th>
                    <td><span className="compare-icon compare-icon-unchecked material-symbols-outlined" aria-label="Not checked">check_box_outline_blank</span></td>
                    <td><span className="compare-icon compare-icon-checked compare-icon-risqly material-symbols-outlined" aria-label="Checked">check_box</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <aside
            className="comparison-cta"
            style={{
              animation: visibleSections.model ? 'slideInUp 0.6s ease-out 0.32s both' : 'none',
              opacity: visibleSections.model ? 1 : 0
            }}
          >
            <p className="comparison-cta-label">Why it matters</p>
            <h4 className="comparison-cta-title">Get a clearer trust signal without a long onboarding cycle.</h4>
            <p className="comparison-cta-text">
              Risqly gives you a shareable rating, deeper vendor-chain visibility, and a faster path to value.
            </p>
            <div className="comparison-cta-actions">
              <button className="btn-kinetic btn-primary-kinetic" onClick={handleJoinClick}>
                Start now
              </button>
              <p className="comparison-cta-note">Setup takes about 10 minutes.</p>
            </div>
          </aside>
        </div>
      </section>

      <section 
        className={`section section-transparent ${visibleSections.whyjoin ? 'visible' : ''}`}
        ref={(el) => (sectionRefs.current.whyjoin = el)}
        id="whyjoin"
        style={{ paddingTop: '80px' }}
      >
        <div className="section-header" style={{
          opacity: visibleSections.whyjoin ? 1 : 0,
          animation: visibleSections.whyjoin ? 'fadeInUp 0.6s ease-out' : 'none',
          transition: 'opacity 0.6s ease-out'
        }}>
          <h2 className="section-label">Security Insights Panel</h2>
          <h3 className="section-title-landing" style={{ color: 'white' }}>Understand the Security Impact of Your Vendor Ecosystem</h3>
        </div>
        <div className="cards-grid cards-3col">
          {[0, 1, 2].map((index) => (
            <div 
              key={index}
              className={`card card-item-${index}`}
              style={{
                animation: visibleSections.whyjoin ? `slideInUp 0.6s ease-out ${0.2 + index * 0.15}s both` : 'none',
                opacity: visibleSections.whyjoin ? 1 : 0
              }}
            >
              {index === 0 && (
                <>
                  <div className="card-icon primary">
                    <span className="material-symbols-outlined">account_tree</span>
                  </div>
                  <h4 className="card-title">Understand Your Inherited Risk</h4>
                  <p className="card-text">
                    Every organization inherits cybersecurity exposure from the vendors it depends on.
                    Risqly models these dependencies so you can see how external relationships
                    influence your security posture.
                  </p>
                </>
              )}
              {index === 1 && (
                <>
                  <div className="card-icon danger">
                    <span className="material-symbols-outlined">trending_up</span>
                  </div>
                  <h4 className="card-title">Identify Which Vendors Matter Most</h4>
                  <p className="card-text">
                    Not all vendors affect your exposure equally. Risqly uses weighted relationships
                    so the vendors that handle more critical data influence your risk profile more strongly,
                    helping your team focus attention where it matters.
                  </p>
                </>
              )}
              {index === 2 && (
                <>
                  <div className="card-icon warning">
                    <span className="material-symbols-outlined">fact_check</span>
                  </div>
                  <h4 className="card-title">Turn Visibility Into Security Improvements</h4>
                  <p className="card-text">
                    Combine vendor-network insights, policy analysis, and outside-in security signals
                    to understand what changes will most improve your organization's resilience.
                  </p>
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Privacy Section */}
      <section 
        className={`section section-transparent ${visibleSections.privacy ? 'visible' : ''}`}
        ref={(el) => (sectionRefs.current.privacy = el)}
        id="privacy"
      >
        <div className="section-header" style={{
          opacity: visibleSections.privacy ? 1 : 0,
          animation: visibleSections.privacy ? 'fadeInUp 0.6s ease-out' : 'none',
          transition: 'opacity 0.6s ease-out'
        }}>
          <h2 className="section-label">Built for Safe Collaboration</h2>
          <h3 className="section-title-landing">Collaborate Without Exposing Vendor Relationships</h3>
          <p className="section-description">
            Risqly is designed so organizations can contribute to ecosystem-level risk insight without revealing confidential vendor relationships.
          </p>
        </div>
        <div className="privacy-grid">
          {['public', 'private'].map((type, idx) => (
            <div 
              key={type}
              className={`privacy-card ${type}`}
              style={{
                animation: visibleSections.privacy ? `slideInUp 0.6s ease-out ${0.2 + idx * 0.15}s both` : 'none',
                opacity: visibleSections.privacy ? 1 : 0
              }}
            >
              <h4>
                {type === 'public' && 'Visible to Everyone'}
                {type === 'private' && 'Visible to Your Team'}
                {type === 'confidential' && 'Always Restricted'}
              </h4>
              <p className="privacy-card-intro">
                {type === 'public' && 'Shared signals that help the wider ecosystem understand exposure and trust.'}
                {type === 'private' && 'Operational detail reserved for internal review and collaboration.'}
              </p>
              <div className="privacy-card-items">
                {type === 'public' && (
                  <>
                    <div className="privacy-card-item">
                      <span className="privacy-card-item-label">Security score</span>
                      <span className="privacy-card-item-text">Public score from 0-100.</span>
                    </div>
                    <div className="privacy-card-item">
                      <span className="privacy-card-item-label">External integrity</span>
                      <span className="privacy-card-item-text">TLS, DNS, email, and exposure signals.</span>
                    </div>
                    <div className="privacy-card-item">
                      <span className="privacy-card-item-label">Policy compliance</span>
                      <span className="privacy-card-item-text">Coverage and governance alignment.</span>
                    </div>
                    <div className="privacy-card-item">
                      <span className="privacy-card-item-label">Identity</span>
                      <span className="privacy-card-item-text">Verification status and public company identity.</span>
                    </div>
                    <div className="privacy-card-item">
                      <span className="privacy-card-item-label">Public profile</span>
                      <span className="privacy-card-item-text">Company name, domain, website, and industry..</span>
                    </div>
                  </>
                )}
                {type === 'private' && (
                  <>
                    <div className="privacy-card-item">
                      <span className="privacy-card-item-label">Policy analysis</span>
                      <span className="privacy-card-item-text">Policy documents and analysis outcomes.</span>
                    </div>
                    <div className="privacy-card-item">
                      <span className="privacy-card-item-label">Scan detail</span>
                      <span className="privacy-card-item-text">Detailed scan results and verification checks.</span>
                    </div>
                    <div className="privacy-card-item">
                      <span className="privacy-card-item-label">Activity logs</span>
                      <span className="privacy-card-item-text">Operational activity and internal contact metadata.</span>
                    </div>
                    <div className="privacy-card-item">
                      <span className="privacy-card-item-label">Vendor context</span>
                      <span className="privacy-card-item-text">Private vendor declarations and dependency weights.</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        <div
          className="privacy-card confidential"
          style={{
            animation: visibleSections.privacy ? 'fadeInUp 0.6s ease-out 0.5s both' : 'none',
            opacity: visibleSections.privacy ? 1 : 0
          }}
        >
          <h4>Always Restricted</h4>
          <p className="privacy-card-intro">
            Highly sensitive ecosystem data stays within approved internal workflows.
          </p>
          <div className="privacy-card-items">
            <div className="privacy-card-item">
              <span className="privacy-card-item-label">Vendor relationships</span>
              <span className="privacy-card-item-text">Never shown publicly.</span>
            </div>
            <div className="privacy-card-item">
              <span className="privacy-card-item-label">Dependency weights</span>
              <span className="privacy-card-item-text">Kept private alongside internal security contacts.</span>
            </div>
            <div className="privacy-card-item">
              <span className="privacy-card-item-label">Restricted access</span>
              <span className="privacy-card-item-text">Visible only to authorized teams and controlled workflows.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Get Started Free Section */}
      <section 
        className={`section section-transparent ${visibleSections.pricing ? 'visible' : ''}`}
        ref={(el) => (sectionRefs.current.pricing = el)}
        id="pricing"
      >
        <div className="section-header" style={{
          opacity: visibleSections.pricing ? 1 : 0,
          animation: visibleSections.pricing ? 'fadeInUp 0.6s ease-out' : 'none',
          transition: 'opacity 0.6s ease-out'
        }}>
          <h2 className="section-label">Plans</h2>
          <h3 className="section-title-landing">Choose how deeply you want to monitor risk</h3>
          <p className="section-description">
            Build your public trust profile for free, or add recurring scans and actionable security intelligence.
          </p>
        </div>

        <div className="pricing-layout">
          <div className="pricing-grid">
            <div 
              className="pricing-card free-card"
              style={{
                animation: visibleSections.pricing ? 'slideInUp 0.6s ease-out 0.2s both' : 'none',
                opacity: visibleSections.pricing ? 1 : 0
              }}
            >
              <div className="pricing-header">
                <h3 className="pricing-title">Free</h3>
                <div className="pricing-price">
                  <strong>€0</strong>
                  <span>forever</span>
                </div>
                <p className="pricing-subtext">Your essential public security profile</p>
              </div>
              <ul className="pricing-features">
                <li>
                  <span className="material-symbols-outlined check-icon">check_circle</span>
                  <span>Public Total Security Score and trust profile</span>
                </li>
                <li>
                  <span className="material-symbols-outlined check-icon">check_circle</span>
                  <span>Company verification and shareable security badge</span>
                </li>
                <li>
                  <span className="material-symbols-outlined check-icon">check_circle</span>
                  <span>Vendor declarations and policy maturity assessment</span>
                </li>
              </ul>
              <button className="btn btn-primary btn-lg" onClick={handleJoinClick}>
                {isAuthenticated ? 'Go to Dashboard' : 'Start Free'}
              </button>
            </div>

            <div 
              className="pricing-card premium-card"
              style={{
                animation: visibleSections.pricing ? 'slideInUp 0.6s ease-out 0.35s both' : 'none',
                opacity: visibleSections.pricing ? 1 : 0
              }}
            >
              <div className="pricing-header premium">
                <span className="premium-badge">14-day free trial</span>
                <h3 className="pricing-title">Risqly Intelligence</h3>
                <div className="pricing-price">
                  <strong>€99</strong>
                  <span>/ month</span>
                </div>
                <p className="pricing-annual">or €990/year, saving €198</p>
                <p className="pricing-subtext">Actionable insights backed by recurring external scans</p>
              </div>
              <ul className="pricing-features">
                <li>
                  <span className="material-symbols-outlined check-icon">check_circle</span>
                  <span>Everything in Free</span>
                </li>
                <li>
                  <span className="material-symbols-outlined check-icon">check_circle</span>
                  <span>Security Insights with prioritized findings and guidance</span>
                </li>
                <li>
                  <span className="material-symbols-outlined check-icon">check_circle</span>
                  <span>Detailed external scan evidence</span>
                </li>
                <li>
                  <span className="material-symbols-outlined check-icon">check_circle</span>
                  <span>Recurring external integrity scans</span>
                </li>
              </ul>
              <button
                className="btn btn-primary btn-lg"
                type="button"
                onClick={handleIntelligenceClick}
              >
                {isAuthenticated ? 'Choose Billing and Pay' : 'Start Free Trial'}
              </button>
              <p className="pricing-checkout-note">Secure checkout and subscription management through Stripe.</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default LandingPage;
